import { ClientWithAuth, setCustomRequestId } from 'client/services/service';
import { extractEnquiryFileName } from 'client/utils/helpers/Enquiry/Enquiry';
import { log } from 'client/utils/logging/logging';
import { getEpsgCode } from 'client/utils/helpers/MapConfig/MapConfig';

const serviceUrlSuffix = '/workpack-request-service';
const mapfishUrlSuffix = '/mapfish';

// new PDFs are stored in blob storage and downloaded via a SAS URL returned as JSON
const downloadNewPdf = async (workpackRequestId, workpackId, title) => {
    try {
        const response = await ClientWithAuth.get(
            `${serviceUrlSuffix}/v2/workpackRequest/${workpackRequestId}/workpack/${workpackId}/download-url`
        );
        const { downloadUrl, expiresInSeconds } = response?.data || {};

        if (!downloadUrl) {
            const invalidDownloadResponseError = new Error('Invalid download response');
            invalidDownloadResponseError.statusCode = 500;
            throw invalidDownloadResponseError;
        }

        window.open(downloadUrl, '_blank');
        log('Workpack', `Download attempt opened for title ${title}`, 'INFO');

        return {
            downloadUrl,
            expiresInSeconds
        };
    } catch (err) {
        log('Workpack', `Exception thrown on workpack download for title ${title}`, 'ERROR');
        throw err;
    }
};

// old PDFs are streamed directly from the API as binary content
const downloadLegacyPdf = async (workpackRequestId, workpackId, title) => {
    try {
        const response = await ClientWithAuth.get(
            `${serviceUrlSuffix}/workpackRequest/${workpackRequestId}/workpack/${workpackId}/download`,
            { responseType: 'blob' }
        );
        const href = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        const fileName = extractEnquiryFileName(response.headers['content-disposition'], title);
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
        log('Workpack', `Download attempt opened for title ${title}`, 'INFO');
    } catch (err) {
        log('Workpack', `Exception thrown on workpack download for title ${title}`, 'ERROR');
        throw err;
    }
};

export default {
    getWorkpackRequest: async (workpackRequestId) => {
        const response = await ClientWithAuth.get(`${serviceUrlSuffix}/workpackRequest/${workpackRequestId}`);
        return {
            ...response.data
        };
    },
    getWorkpackRequests: async (params) => {
        const epsgCode = await getEpsgCode();
        const response = await ClientWithAuth.get(`${serviceUrlSuffix}/${epsgCode}/workpackRequest`, { params });
        return {
            ...response.data
        };
    },
    createWorkpackRequest: async (workpackRequest, requestId) => {
        const epsgCode = await getEpsgCode();
        const headers = requestId ? setCustomRequestId(ClientWithAuth.defaults.headers, requestId) : ClientWithAuth.defaults.headers;
        const { title, workpackOrientation, workpackScale, geometry, electricityStylingOptIn } = workpackRequest;
        const body = {
            title,
            paperSize: workpackOrientation,
            scale: workpackScale,
            geometry,
            electricityStylingOptIn
        };
        const response = await ClientWithAuth.post(
            `${serviceUrlSuffix}/v2/${epsgCode}/workpackRequest`,
            JSON.stringify(body),
            { headers }
        );
        return {
            ...response.data
        };
    },
    deleteWorkpackRequests: async (workpackRequestIds) => {
        const response = await ClientWithAuth.delete(`${serviceUrlSuffix}/workpackRequest`, { data: workpackRequestIds });
        return {
            ...response?.data
        };
    },
    // isNewPdf indicates whether this workpack was generated via the new blob storage approach
    downloadWorkpack: async ({ workpackRequestId, workpackId, title, isNewPdf }) => {
        return isNewPdf
            ? downloadNewPdf(workpackRequestId, workpackId, title)
            : downloadLegacyPdf(workpackRequestId, workpackId, title);
    },
    getPdfCapabilities: async () => {
        const response = await ClientWithAuth.get(`${mapfishUrlSuffix}/capabilities`);
        return {
            layouts: response.data.layouts.map(layout => {
                return {
                    name: layout.name,
                    scales: layout.attributes.find(attr => attr.name === 'map')?.clientInfo.scales || []
                };
            })
        };
    }
};
